import { produce } from 'immer';
import { mergeMap, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { RouterSelectors, ToastService } from '@mitsubishi/core';

import { Navigate } from '@ngxs/router-plugin';
import { Action, State, StateContext, Store } from '@ngxs/store';

import { PROJECT_FEATURE_KEY } from '../../constants';
import { Project, ProjectDetails } from '../../models';
import { ProjectService } from '../../services';
import { ProjectAlternativeState } from '../alternative/alternative.state';
import { ProjectBreadcrumbsState } from '../breadcrumbs/breadcrumbs.state';
import { ProjectConnectedUsersState } from '../connected-users/connected-users.state';
import { SelectionActions } from '../selection/selections.actions';
import { ProjectSelectionsState } from '../selection/selections.state';
import { StudyActions } from '../study/study.actions';
import { ProjectStudyState } from '../study/study.state';
import { ProjectActions } from './project.actions';

export interface ProjectStateModel {
  current: Project;
}

const defaultState: ProjectStateModel = {
  current: null as any,
};

@State<ProjectStateModel>({
  name: PROJECT_FEATURE_KEY,
  defaults: defaultState,
  children: [
    ProjectConnectedUsersState,
    ProjectSelectionsState,
    ProjectBreadcrumbsState,
    ProjectAlternativeState,
    ProjectStudyState,
  ],
})
@Injectable()
export class ProjectState {
  constructor(
    private projectService: ProjectService,
    private toastService: ToastService,
    private store: Store
  ) {}

  @Action(ProjectActions.Reset)
  resetProject(ctx: StateContext<ProjectStateModel>) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      current: null as any,
    });
  }

  @Action(ProjectActions.Load, { cancelUncompleted: true })
  loadProject(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.Load) {
    return this.projectService.checkEnvironment(projectId).pipe(
      switchMap(() => this.projectService.getProject(projectId)),
      tap(project => {
        const state = ctx.getState();
        ctx.patchState({
          ...state,
          current: project,
        });
      }),
      mergeMap((project: Project) => ctx.dispatch(new ProjectActions.LoadSuccess(project)))
    );
  }

  @Action(ProjectActions.Set)
  setProject(ctx: StateContext<ProjectStateModel>, { project }: ProjectActions.Set) {
    ctx.patchState({
      current: project,
    });
  }

  @Action(ProjectActions.AddStudy)
  addStudy(ctx: StateContext<ProjectStateModel>, { study }: ProjectActions.AddStudy) {
    const currentProject = ctx.getState().current;

    ctx.patchState({
      current: {
        ...currentProject,
        studies: {
          ...currentProject.studies,
          [study.uuid]: study,
        },
      },
    });
  }

  @Action(ProjectActions.LoadSuccess)
  loadSuccess(ctx: StateContext<ProjectStateModel>, { project }: ProjectActions.LoadSuccess) {
    ctx.patchState({
      current: project,
    });
  }

  @Action(ProjectActions.UpdateProjectTitle)
  updateProjectTitle(ctx: StateContext<ProjectStateModel>, { projectId, title }: ProjectActions.UpdateProjectTitle) {
    return this.projectService.updateProjectTitle(projectId, title).pipe(
      tap(() => {
        ctx.patchState(
          produce(ctx.getState(), state => {
            state.current.details.title = title;
          })
        );
      })
    );
  }

  @Action(ProjectActions.UpdateState)
  updateProjectState(ctx: StateContext<ProjectStateModel>, { projectDetails }: ProjectActions.UpdateState) {
    ctx.patchState(
      produce(ctx.getState(), draft => {
        draft.current.details = projectDetails as ProjectDetails;
      })
    );
  }

  @Action(ProjectActions.Activate)
  activate(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.Archive) {
    return this.projectService.updateProjectState(projectId, 'ACTIVATE');
  }

  @Action(ProjectActions.Archive)
  archive(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.Archive) {
    return this.projectService.updateProjectState(projectId, 'ARCHIVE').pipe(
      tap(() => {
        this.toastService.showToast({ resourceId: '444890' });
      })
    );
  }

  @Action(ProjectActions.Restore)
  restore(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.Restore) {
    return this.projectService.updateProjectState(projectId, 'RESTORE').pipe(
      tap(() => {
        this.toastService.showToast({ resourceId: '444891' });
      })
    );
  }

  @Action(ProjectActions.AddFavorite)
  setFavorite(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.AddFavorite) {
    return this.projectService.addFavoriteProject(projectId);
  }

  @Action(ProjectActions.RemoveFavorite)
  unFavorite(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.RemoveFavorite) {
    return this.projectService.removeFavoriteProject(projectId);
  }

  @Action(ProjectActions.Duplicate)
  duplicate(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.Duplicate) {
    return this.projectService.duplicateProject(projectId).pipe(
      mergeMap(response => {
        return ctx.dispatch(new Navigate(['project', response.uuid]));
      })
    );
  }

  @Action(ProjectActions.Delete)
  delete(ctx: StateContext<ProjectStateModel>, { projectId }: ProjectActions.Delete) {
    return this.projectService.deleteProject(projectId).pipe(
      mergeMap(() => {
        return ctx.dispatch(new ProjectActions.DeleteSuccess());
      })
    );
  }

  @Action(ProjectActions.DeleteSuccess)
  deleteSuccess(ctx: StateContext<ProjectStateModel>) {
    this.toastService.showToast({ resourceId: '444892' });
    return ctx.dispatch(new Navigate(['/']));
  }

  @Action(ProjectActions.EditProjectCompanies)
  editProjectCompanies(
    ctx: StateContext<ProjectStateModel>,
    { projectId, request }: ProjectActions.EditProjectCompanies
  ) {
    return this.projectService.editProjectCompanies(projectId, request);
  }

  @Action(ProjectActions.UpdateCertification)
  updateCertification(
    ctx: StateContext<ProjectStateModel>,
    { projectId, certification }: ProjectActions.UpdateCertification
  ) {
    return this.projectService.updateCertification(projectId, certification);
  }

  @Action(ProjectActions.UpdateUnitsOfMisures)
  updateUnitsOfMisures(ctx: StateContext<ProjectStateModel>, { projectId, uom }: ProjectActions.UpdateUnitsOfMisures) {
    return this.projectService.updateUnitsOfMisures(projectId, uom);
  }

  @Action(SelectionActions.UpdateStateDetails)
  updateSelectionStateDetails(ctx: StateContext<ProjectStateModel>, { details }: SelectionActions.UpdateStateDetails) {
    const selectionId = this.store.selectSnapshot(RouterSelectors.params)?.selectionId;
    if (!selectionId) return;

    ctx.patchState(
      produce(ctx.getState(), draft => {
        const selection = draft.current.selections[selectionId];
        if (selection) {
          Object.assign(selection.details, details);
        }
      })
    );
  }

  @Action(StudyActions.UpdateCurrencyCode)
  updateStudyCurrencyCode(ctx: StateContext<ProjectStateModel>, { currencyCode }: StudyActions.UpdateCurrencyCode) {
    const studyId = this.store.selectSnapshot(RouterSelectors.params)?.studyId;
    if (!studyId) return;

    ctx.patchState(
      produce(ctx.getState(), draft => {
        const study = draft.current.studies[studyId];
        if (study) {
          study.currencyCode = currencyCode;
        }
      })
    );
  }
}
